import { format } from "date-fns";

// A reusable "filters" model. `fields` describe the filters a screen offers:
//   { key, label, type: "text" | "date" | "select" | "autocomplete", options?: [{ value, label }], placeholder? }
// Each filter is switched on with a checkbox and all switched-on filters apply
// together (AND). State is { [key]: { on, value } }.

const emptyValue = (field) => (field.type === "date" ? null : "");

export const buildEmptyFilters = (fields) =>
  Object.fromEntries(fields.map((field) => [field.key, { on: false, value: emptyValue(field) }]));

export const activeFilterKeys = (filters) => Object.keys(filters).filter((key) => filters[key].on);

// Unticking drops the value too, so re-ticking starts clean.
export const toggleFilter = (filters, key, fields) =>
  filters[key].on
    ? { ...filters, [key]: { on: false, value: emptyValue(fields.find((f) => f.key === key)) } }
    : { ...filters, [key]: { ...filters[key], on: true } };

export const setFilterValue = (filters, key, value) => ({ ...filters, [key]: { ...filters[key], value } });

export const clearAllFilters = (filters, fields) => buildEmptyFilters(fields.filter((f) => f.key in filters));

const toApiDate = (value) => (value instanceof Date && !isNaN(value) ? format(value, "yyyy-MM-dd") : typeof value === "string" ? value : "");
const shortDate = (value) => (value instanceof Date && !isNaN(value) ? format(value, "MM/dd/yyyy") : "");

// The value a filter contributes to the request, or "" while it is off / not filled in.
const apiValue = (field, state) => {
  if (!state?.on) return "";
  if (field.type === "date") return toApiDate(state.value);
  return typeof state.value === "string" ? state.value.trim() : state.value ?? "";
};

// { key: value } for every filter that is on and filled in — ready to become query params.
export const buildFilterQuery = (filters, fields) =>
  fields.reduce((query, field) => {
    const value = apiValue(field, filters[field.key]);
    return value === "" ? query : { ...query, [field.key]: value };
  }, {});

// One removable chip per filter that is on and filled in (a ticked-but-empty filter filters nothing).
export const getFilterChips = (filters, fields) =>
  fields.flatMap((field) => {
    const state = filters[field.key];
    if (!state?.on) return [];
    let text = "";
    if (field.type === "date") text = shortDate(state.value);
    else if (field.type === "autocomplete" || field.type === "select") text = field.options?.find((o) => o.value === state.value)?.label ?? "";
    else text = String(state.value ?? "").trim();
    return text ? [{ key: field.key, label: field.label, text }] : [];
  });
